import { z } from 'zod';

// Define the schema for a line item in the credit note
export const creditNoteItemSchema = z.object({
  id: z.number().optional(), // Only present when editing an existing item
  original_invoice_item_id: z.number().optional(), // Link to the original SI item
  item_id: z.number({ required_error: 'Product is required' }),
  quantity: z.number().min(0.001, { message: 'Return quantity must be greater than 0' }),
  unit_id: z.number({ required_error: 'Unit is required' }),
  rate: z.number().min(0, { message: 'Rate must be 0 or greater' }), // Usually original rate
  discount_percent: z.number().min(0).max(100).default(0),
  tax_rate_id: z.number().nullable().optional(),
  taxable_amount: z.number().optional(), // Will be calculated
  tax_amount: z.number().optional(),     // Will be calculated
  total_amount: z.number().optional(),   // Will be calculated (Frontend: rename to 'amount' for grid)
  description: z.string().optional().nullable(),
  godown_id: z.number({ required_error: 'Warehouse is required' }),
});

// Define the schema for the entire credit note form
export const creditNoteSchema = z.object({
  voucher_type_id: z.number({ required_error: 'Voucher type is required' }), // Will be set to CNOT
  party_id: z.number({ required_error: 'Customer is required' }),
  site_id: z.number().optional().nullable(), // Site from original invoice or user preference
  transaction_date: z.date({ required_error: 'Date is required' }),
  reference_number: z.string().optional().nullable(), // e.g., Original Invoice No.
  reference_invoice_number: z.string().optional().nullable(), // To store original invoice number for backend
  narration: z.string().optional().nullable(),
  linked_voucher_id: z.number().optional().nullable(), // To link back to the original sales invoice
  total_amount: z.number().min(0),
  items: z.array(creditNoteItemSchema)
    .min(1, { message: 'At least one item to return is required' })
    .refine(items => items.every(item => item.quantity > 0), {
      message: 'All returned items must have a quantity greater than zero.',
    }),
});

// Create a type for form values based on the schema
export type CreditNoteFormValues = z.infer<typeof creditNoteSchema>;

// Create a type for line items
export type CreditNoteItemFormValues = z.infer<typeof creditNoteItemSchema>; 